// src/components/LandingPage.jsx
import React, { useEffect, useState } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { motion } from 'framer-motion';
import axios from 'axios';
import './LandingPage.css';
import { useNavigate } from 'react-router-dom';
import { useDynamicFavicon } from './hooks/useBranding';
import logoKasir from './ImgLanding/kasir.jpeg';
import { apiHost } from './utils/apiHost';
import { alamatAplikasi } from './utils/domain';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

const DEFAULT_HERO = {
  badge: 'POS untuk Restoran, Cafe & Toko Retail',
  title: 'Kasir Online untuk Restoran/Cafe & Retail Anda',
  subtitle: 'Kelola transaksi kasir, stok produk, laporan penjualan, hingga pemesanan mandiri pelanggan dalam satu platform terintegrasi — mendukung mode Cafe (meja & menu QR) maupun Retail (scan barcode).',
};

const LandingPage = () => {
  const navigate = useNavigate();
  useDynamicFavicon();
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [showcase, setShowcase] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [features, setFeatures] = useState([]);
  const [split1, setSplit1] = useState(null);
  const [split2, setSplit2] = useState(null);
  const [stats, setStats] = useState([]);
  const [cta, setCta] = useState(null);

  useEffect(() => {
    axios.get(`${getApiBaseUrl()}/getpengaturanutama`)
      .then((res) => {
        const data = res.data?.data;
        if (!data) return;
        setHero({
          badge: data.webUtamaHeroBadge || DEFAULT_HERO.badge,
          title: data.webUtamaHeroTitle || DEFAULT_HERO.title,
          subtitle: data.webUtamaHeroSubtitle || DEFAULT_HERO.subtitle,
        });
        if (data.logo) setLogoUrl(`${getApiBaseUrl()}/uploads/${data.logo}`);
        setPricingPlans(Array.isArray(data.webUtamaPricingPlans) ? data.webUtamaPricingPlans : []);
        setTestimonials(Array.isArray(data.webUtamaTestimonials) ? data.webUtamaTestimonials : []);

        const slots = [
          { n: 1, frame: 'browser' },
          { n: 2, frame: 'phone' },
          { n: 3, frame: 'phone' },
        ]
          .filter((s) => data[`webUtamaShowcase${s.n}Image`])
          .map((s) => ({
            frame: s.frame,
            image: `${getApiBaseUrl()}/uploads/${data[`webUtamaShowcase${s.n}Image`]}`,
            tag: data[`webUtamaShowcase${s.n}Tag`] || '',
            caption: data[`webUtamaShowcase${s.n}Caption`] || '',
          }));
        setShowcase(slots);
        setFeatures(Array.isArray(data.webUtamaFeatures) ? data.webUtamaFeatures : []);
        setSplit1(data.webUtamaSplit1 || null);
        setSplit2(data.webUtamaSplit2 || null);
        setStats(Array.isArray(data.webUtamaStats) ? data.webUtamaStats : []);
        setCta(data.webUtamaCta || null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Smooth scroll for anchor links
    const handleAnchorClick = (e) => {
      const target = e.target.closest('a');
      if (target && target.hash && target.hash.startsWith('#')) {
        const element = document.querySelector(target.hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  const handleNavigateToLogin = () => {
    const app = alamatAplikasi(); // di juragankuliner.com: masuk lewat app.juragankuliner.com
    if (app) window.location.assign(`${app}/login`);
    else navigate('/login');
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 }
  };

  const today = new Date();
  const dateTime = today.getFullYear();

  return (
    <>
      {/* NAV */}
      <nav className="landing-nav">
        <a href="#" className="nav-logo">
          <div className="nav-logo-icon">
            <img
              src={logoUrl || logoKasir}
              alt="Logo Juragan Kuliner"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
            />
          </div>
          Juragan Kuliner
        </a>
        <div className="nav-links">
          <ScrollLink to="fitur" smooth={true} duration={500}>Fitur</ScrollLink>
          <ScrollLink to="cara-kerja" smooth={true} duration={500}>Cara Kerja</ScrollLink>
          <ScrollLink to="teknologi" smooth={true} duration={500}>Teknologi</ScrollLink>
          <ScrollLink to="harga" smooth={true} duration={500}>Harga</ScrollLink>
        </div>
        <button onClick={handleNavigateToLogin} className="nav-cta">
          Mulai Demo →
        </button>
      </nav>

      {/* HERO */}
      <section className="hero-section" style={{ padding: 0 }}>
        <div className="hero">
          <div>
            <motion.div
              className="hero-badge"
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 0.5 }}
            >
              <span>🧾</span> {hero.badge}
            </motion.div>
            <motion.h1
              className="hero-title"
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {hero.title === DEFAULT_HERO.title ? (
                <>Kasir Online untuk<br /><em>Restoran/Cafe & Retail Anda</em></>
              ) : (
                hero.title
              )}
            </motion.h1>
            <motion.p
              className="hero-sub"
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {hero.subtitle}
            </motion.p>
            <motion.div
              className="hero-actions"
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <button onClick={handleNavigateToLogin} className="btn-primary">
                Coba Demo
              </button>
              <ScrollLink to="fitur" smooth={true} duration={500} className="btn-secondary">
                Lihat Fitur ↓
              </ScrollLink>
            </motion.div>
            <motion.div
              className="hero-trust"
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="hero-avatars">
                <div className="av">BS</div>
                <div className="av">RW</div>
                <div className="av">AN</div>
              </div>
              <p>Dipercaya oleh <strong>200+ restoran, cafe, dan toko</strong> di Indonesia</p>
            </motion.div>
          </div>

          <div className="hero-visual">
            {/* Dashboard mockup */}
            <div className="dashboard-mockup">
              <div className="mockup-topbar">
                <div className="mockup-dots">
                  <span></span><span></span><span></span>
                </div>
                <div className="mockup-title">Juragan Kuliner Dashboard</div>
                <div className="mockup-time">Senin, 28 Sep</div>
              </div>
              <div className="mockup-body">
                <div className="mockup-stats">
                  <div className="stat-card">
                    <div className="stat-label">Penjualan</div>
                    <div className="stat-val green">Rp 12,8jt</div>
                    <div className="stat-trend">↑ 12,4%</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Transaksi</div>
                    <div className="stat-val">284</div>
                    <div className="stat-trend">↑ 8,2%</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-label">Produk Terjual</div>
                    <div className="stat-val gold">642</div>
                    <div className="stat-trend">↑ 5,6%</div>
                  </div>
                </div>
                <div className="mockup-txn">
                  <div className="txn-header">
                    <span>Transaksi Terbaru</span>
                    <a href="#">Lihat semua</a>
                  </div>
                  {[
                    { icon: '🍜', name: 'Meja 12', time: '2 item', amt: 'Rp185K', badge: 'Lunas', badgeClass: 'badge-green' },
                    { icon: '☕', name: 'Meja 07', time: '4 item', amt: 'Rp320K', badge: 'Lunas', badgeClass: 'badge-green' },
                    { icon: '🛍️', name: 'Take Away', time: '3 item', amt: 'Rp145K', badge: 'QRIS', badgeClass: 'badge-gold' },
                  ].map((t, i) => (
                    <div className="txn-row" key={i}>
                      <div className={`txn-icon ${i === 2 ? 'gold' : ''}`}>{t.icon}</div>
                      <div className="txn-info">
                        <div className="txn-name">{t.name}</div>
                        <div className="txn-time">{t.time}</div>
                      </div>
                      <div className="txn-amt">{t.amt}</div>
                      <span className={`txn-badge ${t.badgeClass}`}>{t.badge}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating cards */}
            <div className="float-card float-scan">
              <div className="float-card-label">Menu &amp; Pembayaran</div>
              <div className="float-scan-code">
                <div className="scan-icon">▦</div>
                <div className="scan-info">
                  <p>Scan QR di Meja</p>
                  <span>Pesan langsung dari HP</span>
                </div>
              </div>
            </div>
            <div className="float-card float-notif">
              <div className="float-card-label">Notifikasi</div>
              <div className="float-notif-row">
                <div className="notif-dot"></div>
                <div className="notif-text">
                  <strong>Pembayaran berhasil</strong><br />
                  QRIS · Rp 185.000 diterima
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      {features.length > 0 && (
        <section className="features-bg" id="fitur">
          <div className="container">
            <div className="section-header">
              <div className="section-label">Fitur Lengkap</div>
              <h2 className="section-title">Semua yang dibutuhkan<br /><em>Restoran, Cafe &amp; Toko Anda</em></h2>
              <p className="section-sub">Dari kasir sampai laporan penjualan, Juragan Kuliner hadir dengan fitur lengkap yang dirancang khusus untuk bisnis kuliner dan retail.</p>
            </div>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div className="feat-card" key={index}>
                  <div className={`feat-icon ${feature.iconClass}`}>{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  <div className="feat-tags">
                    {(feature.tags || []).map((tag, i) => (
                      <span className="feat-tag" key={i}>{tag}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPLIT 1 — KASIR TRANSAKSI */}
      {split1 && (
        <section id="cara-kerja">
          <div className="container">
            <div className="split">
              <div className="split-text">
                {split1.label && <div className="section-label">{split1.label}</div>}
                <h2>{split1.heading}</h2>
                <p>{split1.description}</p>
                <ul className="split-list">
                  {(split1.bullets || []).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                {split1.buttonText && (
                  <button onClick={handleNavigateToLogin} className="btn-primary" style={{ border: 'none', cursor: 'pointer' }}>{split1.buttonText}</button>
                )}
              </div>
              <div className="split-visual">
                <div className="panel-visual">
                  <div className="panel-topbar">
                    <div className="panel-topbar-dot"></div>
                    <div className="panel-topbar-dot"></div>
                    <div className="panel-topbar-dot"></div>
                    <span className="panel-topbar-title">Transaksi — Meja 04</span>
                  </div>
                  <div className="panel-body">
                    <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 8, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Item Pesanan</div>
                    {orderItems.map((item, index) => (
                      <div className="loan-book-row" key={index}>
                        <div className={`loan-book-cover ${item.coverClass}`}>{item.icon}</div>
                        <div className="loan-book-info">
                          <div className="loan-book-title">{item.name}</div>
                          <div className="loan-book-due">{item.meta}</div>
                        </div>
                        <span className={`loan-status ${item.statusClass}`}>{item.price}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 16, padding: 12, background: 'var(--accent)', borderRadius: 8, textAlign: 'center', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                      ✓ Proses Pembayaran &amp; Cetak Struk
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SPLIT 2 — LAPORAN */}
      {split2 && (
        <section style={{ background: 'var(--paper-warm)' }}>
          <div className="container">
            <div className="split reverse">
              <div className="split-text">
                {split2.label && <div className="section-label">{split2.label}</div>}
                <h2>{split2.heading}</h2>
                <p>{split2.description}</p>
                <ul className="split-list">
                  {(split2.bullets || []).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                {split2.buttonText && (
                  <ScrollLink to="harga" smooth={true} duration={500} className="btn-primary">{split2.buttonText}</ScrollLink>
                )}
              </div>
              <div className="split-visual">
              <div className="panel-visual">
                <div className="panel-topbar">
                  <div className="panel-topbar-dot"></div>
                  <div className="panel-topbar-dot"></div>
                  <div className="panel-topbar-dot"></div>
                  <span className="panel-topbar-title">Laporan Penjualan — September 2026</span>
                </div>
                <div className="panel-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--ink)' }}>Rp 4.750.000</div>
                      <div style={{ fontSize: 12, color: 'var(--accent)' }}>↑ 18% vs bulan lalu</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: 'var(--ink-muted)' }}>Total Transaksi</div>
                      <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--ink)' }}>1.247</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Kategori Terlaris</div>
                  <div className="report-bars">
                    {reportData.map((item, index) => (
                      <div className="report-bar-item" key={index}>
                        <span className="report-bar-label">{item.label}</span>
                        <div className="report-bar-track">
                          <div className={`report-bar-fill ${item.fillClass}`} style={{ width: `${item.percentage}%` }}></div>
                        </div>
                        <span className="report-bar-val">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="report-footer">
                    <span>Data diperbarui otomatis</span>
                    <div className="report-export">⬇ Ekspor Excel</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* STATS */}
      {stats.length > 0 && (
      <div className="stats-band">
        <div className="container">
          {stats.map((stat, index) => (
            <div className="stat-item" key={index}>
              <div className="stat-number">{stat.number}</div>
              <div className="stat-desc">{stat.desc}</div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* TEKNOLOGI — SCREENSHOT ASLI APLIKASI */}
      {showcase.length > 0 && (
        <section className="showcase-bg" id="teknologi">
          <div className="container">
            <div className="section-header">
              <div className="section-label">Teknologi</div>
              <h2 className="section-title">Satu sistem,<br /><em>siap untuk semua mode bisnis</em></h2>
              <p className="section-sub">Tampilan asli aplikasi Juragan Kuliner — kasir mobile Android untuk Retail &amp; Cafe, serta kasir web yang bisa diakses langsung dari browser.</p>
            </div>

            <div className="showcase-grid">
              {showcase.map((item, index) => (
                <div className="showcase-item" key={index}>
                  {item.tag && <span className="showcase-tag">{item.tag}</span>}
                  {item.frame === 'browser' ? (
                    <div className="frame-browser">
                      <div className="frame-browser-bar"><span></span><span></span><span></span></div>
                      <img src={item.image} alt={item.tag || 'Screenshot aplikasi'} />
                    </div>
                  ) : (
                    <div className="frame-phone">
                      <div className="frame-phone-notch"></div>
                      <div className="frame-phone-screen">
                        <img src={item.image} alt={item.tag || 'Screenshot aplikasi'} />
                      </div>
                    </div>
                  )}
                  {item.caption && <div className="showcase-caption">{item.caption}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section>
          <div className="container">
            <div className="section-header" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="section-label">Testimoni</div>
              <h2 className="section-title">Kata mereka yang<br /><em>sudah pakai Juragan Kuliner</em></h2>
            </div>
            <div className="testimonial-grid">
              {testimonials.map((t, index) => (
                <div className="testimonial-card" key={index}>
                  <div className="testimonial-stars">★★★★★</div>
                  <p className="testimonial-quote">&ldquo;{t.quote}&rdquo;</p>
                  <div className="testimonial-author">
                    <div className="loan-av">{t.initials}</div>
                    <div>
                      <div className="testimonial-name">{t.name}</div>
                      <div className="testimonial-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRICING */}
      {pricingPlans.length > 0 && (
        <section className="features-bg" id="harga">
          <div className="container">
            <div className="section-header" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="section-label">Harga</div>
              <h2 className="section-title">Pilih paket yang<br /><em>sesuai kebutuhan</em></h2>
            </div>
            <div className="pricing-grid">
              {pricingPlans.map((plan, index) => (
                <div className={`price-card ${plan.featured ? 'featured' : ''}`} key={index}>
                  {plan.badge && <div className="price-badge">{plan.badge}</div>}
                  <div className="price-name">{plan.name}</div>
                  <div className="price-amount">{plan.price}</div>
                  <div className="price-period">{plan.period}</div>
                  <div className="price-divider"></div>
                  <ul className="price-features">
                    {(plan.features || []).map((feature, i) => (
                      <li key={i} className={feature.na ? 'na' : ''}>{feature.text}</li>
                    ))}
                  </ul>
                  <button onClick={handleNavigateToLogin} className={`btn-price ${plan.featured ? 'btn-price-white' : 'btn-price-outline'}`} style={{ border: 'none', cursor: 'pointer', width: '100%' }}>{plan.btnText}</button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {cta && (
        <section className="cta-band">
          <div className="container">
            <h2>{cta.heading}</h2>
            <p>{cta.description}</p>
            <div className="cta-actions">
              {cta.primaryText && (
                <button onClick={handleNavigateToLogin} className="btn-primary" style={{ fontSize: 16, padding: '16px 32px', border: 'none', cursor: 'pointer' }}>
                  {cta.primaryText}
                </button>
              )}
              {cta.secondaryText && (
                <a href="#" className="btn-secondary" style={{ fontSize: 16, padding: '16px 28px' }}>
                  {cta.secondaryText}
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer>
        <div className="footer-inner">
          <div className="footer-brand">
            <a href="#" className="nav-logo">
              <div className="nav-logo-icon" style={{ background: 'rgba(255,255,255,0.15)' }}>
                <img
                  src={logoUrl || logoKasir}
                  alt="Logo Juragan Kuliner"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                />
              </div>
              Juragan Kuliner
            </a>
            <p>Sistem kasir Restoran/Cafe dan toko retail modern yang membantu Anda mengelola produk, transaksi, dan laporan dengan efisien dan akurat.</p>
          </div>
          <div className="footer-col">
            <h4>Produk</h4>
            <ul>
              <li><ScrollLink to="fitur" smooth={true} duration={500}>Fitur</ScrollLink></li>
              <li><ScrollLink to="harga" smooth={true} duration={500}>Harga</ScrollLink></li>
              <li><a href="#">Changelog</a></li>
              <li><a href="#">Roadmap</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Sumber Daya</h4>
            <ul>
              <li><a href="#">Dokumentasi</a></li>
              <li><a href="#">Tutorial</a></li>
              <li><a href="#">Blog</a></li>
              <li><a href="#">Status</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Perusahaan</h4>
            <ul>
              <li><a href="#">Tentang Kami</a></li>
              <li><a href="#">Karir</a></li>
              <li><a href="#">Kontak</a></li>
              <li><a href="#">Kebijakan Privasi</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span>©{dateTime} Juragan Kuliner. Hak cipta dilindungi undang-undang.</span>
        </div>
      </footer>
    </>
  );
};

// Data dekoratif (mockup visual, bukan konten yang diedit lewat Pengaturan Homepage)
const orderItems = [
  { icon: '🍜', coverClass: '', name: 'Mie Goreng Spesial', meta: '1x · Rp 25.000', price: 'Rp 25.000', statusClass: 'ok' },
  { icon: '🥤', coverClass: 'gold', name: 'Es Teh Manis', meta: '2x · Rp 8.000', price: 'Rp 16.000', statusClass: 'ok' },
  { icon: '🍗', coverClass: 'purple', name: 'Ayam Bakar', meta: '1x · Rp 35.000', price: 'Rp 35.000', statusClass: 'ok' }
];

const reportData = [
  { label: 'Makanan', percentage: 82, value: '412', fillClass: '' },
  { label: 'Minuman', percentage: 65, value: '328', fillClass: '' },
  { label: 'Snack', percentage: 50, value: '251', fillClass: 'gold' },
  { label: 'Dessert', percentage: 38, value: '189', fillClass: '' },
  { label: 'Lainnya', percentage: 26, value: '130', fillClass: 'gold' }
];

export default LandingPage;
